import { ActivatedRoute, Router } from '@angular/router';
import { parse, required, stringify } from '@atmi/core/utils';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { SwalService } from '@atmi/core';

import { TablePageEvent, TableSelection, TemplateField } from './table';

interface ReferenceConstraintItem {
  id: string;
  type: string;
  code?: string;
}

interface ReferenceConstraintDetails {
  constraint?: string;
  sourceType?: string;
  sourceId?: string;
  references?: ReferenceConstraintItem[];
}

const REFERENCE_TYPE_TO_ROUTE_PATH: Record<string, string> = {
  BeneOA: 'cards/oa/manager',
  BeneS: 'cards/s/manager',
  BeneF: 'cards/f/manager',
  BeneD: 'cards/d/manager',
  BeneMI: 'cards/mi/manager',
};

/**@TODO set deafault values os const readonly for filters in tables */
export abstract class BaseTableComponent<T extends { id?: string }> {
  selectedItems: T[] = [];
  managerPath = 'manager';

  readonly pageInfo$ = new BehaviorSubject({ current: 0, limit: 10 });
  set pageInfo(value: { current: number, limit: number }) {
    this.pageInfo$.next(value);
  }
  get pageInfo() {
    return this.pageInfo$.value;
  }

  readonly page$: Observable<{ current: number, limit: number }>;

  readonly reload$ = new BehaviorSubject(true);

  get selectedItemIds() {
    return this.selectedItems
      .map((s) => s.id)
      .filter((id): id is string => !!id);
  }

  defaultLimit: number = 10;

  constructor(
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
    managerPath?: string,
    protected readonly paginationStore: 'route' | 'local' | 'none' = 'route',
  ) {
    this.managerPath = managerPath ?? this.managerPath;

    this.page$ = of(this.paginationStore).pipe(
      switchMap((store) => store === 'route'
        ? this.route.queryParams.pipe(
          map((params) => params.page),
          map((page) => page || stringify({ current: 0, limit: this.defaultLimit })),
          map((page) => parse<{ current: number, limit: number }>(page)),
        )
        : this.pageInfo$.asObservable(),
      ),
      map((q) => q),
    );
  }

  applyChangePageHandler(pageEvent: TablePageEvent) {

    if (this.paginationStore === 'route') {
      const page = JSON.stringify({ current: pageEvent.pageIndex, limit: pageEvent.pageSize });
      const queryParams = { page };

      this.router.navigate(['./'], { relativeTo: this.route, queryParamsHandling: 'merge', queryParams });
    } else {
      this.pageInfo = { current: pageEvent.pageIndex, limit: pageEvent.pageSize };
      this.reload$.next(true);
    }
  }

  selectHandler(selectedNews: TableSelection<T> | any) {
    if (selectedNews instanceof Event || !selectedNews || !Array.isArray(selectedNews.selected)) {
      return;
    }
    this.selectedItems = selectedNews.selected;
  }

  addHandler() {
    if (!this.managerPath) {
      throw new Error('Cannot navigate to a manager view without a valid path');
    }

    this.router.navigate([this.managerPath], { relativeTo: this.route });
  }

  async deleteHandler(
    id: string,
    item?: T,
    options = {
      text: 'Sei sicuro di voler procedere?',
      title: 'Eliminazione in corso'
    }) {

    try {
      const confirmResult = await this.swalService.showConfirm({
        text: this.translate.instant(options.text),
        title: this.translate.instant(options.title),
      });

      if (confirmResult) {
        await firstValueFrom(this.deleteConcreteHandler(id, item).pipe(first()));
        this.reload$.next(true);
        this.selectedItems = [];
      }

    } catch (e: any) {
      const referenceConstraint = this.extractReferenceConstraintDetails(e);
      if (referenceConstraint) {
        await this.showReferenceConstraintError(referenceConstraint);
      }
    }
  }

  async deleteSelectedHandler() {
    const selectedIds = this.selectedItems
      .map((m) => m.id)
      .filter((id): id is string => !!id);

    try {
      await firstValueFrom(this.deleteSelectedConcreteHandler(selectedIds).pipe(first()));

      this.reload$.next(true);
      this.selectedItems = [];
    } catch (e: any) {
      const referenceConstraint = this.extractReferenceConstraintDetails(e);
      if (referenceConstraint) {
        await this.showReferenceConstraintError(referenceConstraint);
      } else {
        throw e;
      }
    }
  }

  abstract deleteConcreteHandler(id: string, item?: T): Observable<any>; // Template method pattern
  abstract deleteSelectedConcreteHandler(ids: Array<T['id']>): Observable<any>;

  editShowHandler(item: T) {
    if (!item.id) {
      console.warn('Cannot navigate to edit view: item.id is undefined', item);
      return Promise.resolve(false);
    }
    return this.router.navigate([this.managerPath, item.id], { relativeTo: this.route });
  }

  private extractReferenceConstraintDetails(
    error: any,
  ): ReferenceConstraintDetails | undefined {
    const details =
      error?.error?.error?.details ||
      error?.error?.details;

    if (!details || !Array.isArray(details.references) || details.references.length === 0) {
      return undefined;
    }

    return details as ReferenceConstraintDetails;
  }

  private escapeReferenceHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private buildReferenceUrl(type: string, id: string): string | undefined {
    const routePath = REFERENCE_TYPE_TO_ROUTE_PATH[type];
    if (!routePath) {
      return undefined;
    }

    const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
    const baseUrl = new URL(baseHref, window.location.origin);
    return new URL(`${routePath}/${encodeURIComponent(id)}`, baseUrl).toString();
  }

  private buildReferenceConstraintHtml(details: ReferenceConstraintDetails): string {
    const grouped = (details.references || []).reduce(
      (acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }
        acc[item.type].push(item);
        return acc;
      },
      {} as Record<string, ReferenceConstraintItem[]>,
    );

    const sourceIdHtml = details.sourceId
      ? `<p><strong>ID fonte:</strong> ${this.escapeReferenceHtml(details.sourceId)}</p>`
      : '';

    const groupedHtml = Object.entries(grouped)
      .map(([type, items]) => {
        const listItems = items
          .map((item) => {
            const link = this.buildReferenceUrl(type, item.id);
            const label = this.escapeReferenceHtml(item.code || item.id);
            const idHtml = this.escapeReferenceHtml(item.id);
            if (link) {
              return `<li><a target="_blank" rel="noopener noreferrer" href="${link}">${label}</a> <small>(${idHtml})</small></li>`;
            }
            return `<li>${label} <small>(${idHtml})</small></li>`;
          })
          .join('');

        return `<h4>${this.escapeReferenceHtml(type)}</h4><ul>${listItems}</ul>`;
      })
      .join('');

    return `<div class="reference-constraint-error">${sourceIdHtml}${groupedHtml}</div>`;
  }

  private async showReferenceConstraintError(
    referenceConstraint: ReferenceConstraintDetails,
  ): Promise<void> {
    const html = this.buildReferenceConstraintHtml(referenceConstraint);
    const sourceLabel = referenceConstraint.sourceType || 'Risorsa';
    await this.swalService.showError({
      title: 'Eliminazione non consentita',
      text: `${sourceLabel} referenziata da altri oggetti.`,
      html,
    });
  }

  manageRowActions(event: { type: TemplateField, item: T }) {
    switch (event.type) {
      case 'edit':
      case 'show':
        return this.editShowHandler(event.item);
      case 'delete':
        required(event.item.id);
        return this.deleteHandler(event.item.id, event.item);
      default:
        return;
    }
  }
}
